import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import {
  Heart, Calendar, ArrowLeft, Sparkle, CheckCircle, Clock, Moon, Info, Star,
  Question, X, MagnifyingGlass, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PremiumTeaser from "../components/PremiumTeaser";

const YEARS = [2026, 2027, 2028, 2029, 2030];

const emptyPartner = { date: "", time: "", place: "", timezone: "Asia/Kolkata", name: "" };

export default function MarriageMuhuratPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(2026);
  const [boy, setBoy] = useState({ ...emptyPartner });
  const [girl, setGirl] = useState({ ...emptyPartner });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // "Ask any date" panel state
  const [askOpen, setAskOpen] = useState(false);
  const [askDate, setAskDate] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askResult, setAskResult] = useState(null);

  const update = (setter) => (k) => (v) => setter((f) => ({ ...f, [k]: v }));
  const boyU = update(setBoy);
  const girlU = update(setGirl);

  const scan = async (e) => {
    e?.preventDefault();
    for (const [label, p] of [["Boy", boy], ["Girl", girl]]) {
      if (!p.date || !p.time || !p.place) {
        toast.error(`Please fill ${label.toLowerCase()}'s date, time and place of birth.`);
        return;
      }
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/marriage-muhurat/scan", { year, boy, girl });
      setResult(data);
      setTimeout(() => document.querySelector("[data-testid='mm-result']")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute muhurat dates.");
    } finally { setBusy(false); }
  };

  const checkDate = async (e) => {
    e?.preventDefault();
    if (!askDate) { toast.error("Pick a date first."); return; }
    for (const [label, p] of [["Boy", boy], ["Girl", girl]]) {
      if (!p.date || !p.time || !p.place) {
        toast.error(`Please fill ${label.toLowerCase()}'s birth details first.`);
        return;
      }
    }
    setAskBusy(true);
    setAskResult(null);
    try {
      const { data } = await api.post("/marriage-muhurat/check-date",
        { date: askDate, boy, girl });
      setAskResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check that date.");
    } finally { setAskBusy(false); }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="marriage-muhurat-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Heart size={14} weight="duotone" /> Free · Marriage Muhurat Calculator
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Marriage Muhurat · <span className="italic text-gold">auspicious wedding dates</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-3xl font-light">
          Enter both partners&apos; birth details and pick a year (2026–2030). We scan every day of
          the year and return only the dates that pass all classical filters — solar month,
          nakshatra, tithi, yoga, karana, weekday, Guru/Shukra combustion & debilitation, and
          most importantly <b>Chandrashtama for BOTH partners</b> (Moon not in 8th from either
          natal Moon).
        </p>

        {/* FORM */}
        <form onSubmit={scan} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-6">
          {/* Year */}
          <label className="block max-w-xs">
            <span className="label-eyebrow text-xs">Marriage year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="mm-year">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>

          {/* Both partners */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { key: "boy", data: boy, up: boyU, title: "Groom's birth details", accent: "text-blue-800" },
              { key: "girl", data: girl, up: girlU, title: "Bride's birth details", accent: "text-rose-800" },
            ].map(({ key, data, up, title, accent }) => (
              <div key={key} className="rounded-lg border border-soft bg-white/60 p-4 space-y-3">
                <div className={`text-sm font-semibold ${accent}`}>{title}</div>
                <label className="block">
                  <span className="label-eyebrow text-xs">Name (optional)</span>
                  <input value={data.name} onChange={(e) => up("name")(e.target.value)}
                    className="mt-1.5 w-full px-3 py-2 rounded-md border border-soft bg-bg text-sm"
                    data-testid={`mm-${key}-name`} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="label-eyebrow text-xs">Date</span>
                    <input type="date" required value={data.date} onChange={(e) => up("date")(e.target.value)}
                      className="mt-1.5 w-full px-2 py-2 rounded-md border border-soft bg-bg text-sm"
                      data-testid={`mm-${key}-date`} />
                  </label>
                  <label className="block">
                    <span className="label-eyebrow text-xs">Time</span>
                    <input type="time" required value={data.time} onChange={(e) => up("time")(e.target.value)}
                      className="mt-1.5 w-full px-2 py-2 rounded-md border border-soft bg-bg text-sm"
                      data-testid={`mm-${key}-time`} />
                  </label>
                </div>
                <label className="block">
                  <span className="label-eyebrow text-xs">Place</span>
                  <div className="mt-1.5">
                    <PlaceAutocompleteInput
                      value={data.place}
                      onChange={(v) => up("place")(v)}
                      onPick={({ name, timezone }) => { up("place")(name); if (timezone) up("timezone")(timezone); }}
                      placeholder="City of birth"
                      testId={`mm-${key}-place`}
                    />
                  </div>
                </label>
              </div>
            ))}
          </div>

          <button type="submit" disabled={busy}
            className="btn-primary rounded-md w-full sm:w-auto px-6 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="mm-submit">
            <Sparkle size={16} weight="duotone" />
            {busy ? `Scanning ${year}…` : `Find auspicious dates in ${year}`}
          </button>
        </form>

        {/* RESULT */}
        {result && (
          <section className="mt-10 space-y-5" data-testid="mm-result">
            {/* Summary */}
            <div className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SumCard icon={<Calendar size={16} weight="duotone"/>} label="Auspicious dates found"
                  value={result.total_auspicious_dates}
                  note={`${result.regular_dates_count || 0} classical + ${result.abuja_dates_count || 0} abuja · in ${result.year}`} />
                <SumCard icon={<Moon size={16} weight="duotone"/>} label={`${result.boy.name || "Groom"} · Chandrashtama`}
                  value={result.boy.chandrashtama_sign} note={`natal Moon: ${result.boy.moon_rashi}`} />
                <SumCard icon={<Moon size={16} weight="duotone"/>} label={`${result.girl.name || "Bride"} · Chandrashtama`}
                  value={result.girl.chandrashtama_sign} note={`natal Moon: ${result.girl.moon_rashi}`} />
              </div>
              <p className="text-[11px] text-muted italic mt-4">{result.method_note}</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.dates.map((r) => (
                <div key={r.date}
                  className={`rounded-xl border p-4 ${r.is_abuja ? "border-amber-300 bg-amber-50/70" : "border-emerald-200 bg-emerald-50/40"}`}
                  data-testid={`mm-date-${r.date}`}>
                  <div className="flex items-center justify-between">
                    <div className={`font-serif-display text-lg ${r.is_abuja ? "text-amber-900" : "text-emerald-900"}`}>
                      {new Date(r.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                    </div>
                    {r.is_abuja
                      ? <Star size={18} weight="fill" className="text-amber-600" />
                      : <CheckCircle size={18} weight="fill" className="text-emerald-600" />}
                  </div>
                  {r.is_abuja && (
                    <div className="mt-1 text-xs font-semibold text-amber-800">
                      Abuja Muhurat · {r.abuja_name}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-[#3F2D1B] space-y-0.5">
                    <div><b>Tithi:</b> {r.tithi}</div>
                    <div><b>Nakshatra:</b> {r.nakshatra}</div>
                    <div><b>Sun in:</b> {r.sun_sign} · <b>Moon in:</b> {r.moon_sign}</div>
                    <div><b>Yoga:</b> {r.yoga} · <b>Karana:</b> {r.karana}</div>
                  </div>
                  {r.best_muhurat_window && (
                    <div className="mt-3 rounded-md bg-white border border-amber-200 px-3 py-2 flex items-center gap-2">
                      <Clock size={14} weight="duotone" className="text-amber-700 shrink-0" />
                      <div className="text-xs">
                        <b>Best window:</b> {r.best_muhurat_window.start} – {r.best_muhurat_window.end}
                        <span className="text-muted ml-1">({r.best_muhurat_window.label})</span>
                      </div>
                    </div>
                  )}
                  {r.abuja_caveat && (
                    <div className="mt-2 rounded-md bg-white/70 border border-amber-300 px-2.5 py-1.5 text-[11px] text-amber-900">
                      <b>Caveat:</b> {r.abuja_caveat}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded ${r.chandrashtama_boy_clear ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {result.boy.name || "Boy"} chandrashtama clear
                    </span>
                    <span className={`px-1.5 py-0.5 rounded ${r.chandrashtama_girl_clear ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {result.girl.name || "Girl"} chandrashtama clear
                    </span>
                    {r.is_abuja
                      ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Abuja (self-siddha)</span>
                      : <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Guru/Shukra clear</span>}
                  </div>
                </div>
              ))}
              {result.dates.length === 0 && (
                <div className="col-span-full rounded-xl border border-rose-200 bg-rose-50/50 p-5 text-sm text-rose-900">
                  No dates in {result.year} pass ALL filters for this couple. Try the next year — Chandrashtama
                  and solar-month overlaps shift year-on-year, so a different year usually opens up options.
                </div>
              )}
            </div>

            {/* Monthly coverage — explains empty months */}
            {result.monthly_coverage && (
              <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="mm-monthly-coverage">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={16} weight="duotone" className="text-gold" />
                  <div className="font-serif-display text-lg">Month-by-month coverage in {result.year}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs">
                  {result.monthly_coverage.map((m) => (
                    <div key={m.month}
                      className={`rounded-md border px-2.5 py-2 ${m.passed > 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50/50 border-rose-200"}`}>
                      <div className="font-semibold text-[#3F2D1B]">{m.month}</div>
                      {m.passed > 0
                        ? <div className="text-emerald-800">{m.passed} auspicious day{m.passed > 1 ? "s" : ""}</div>
                        : <div className="text-rose-800 text-[11px]">
                            Blocked · <span className="italic">{m.primary_blocker || "no filter passed"}</span>
                          </div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Navratri caveat */}
            {result.navratri_note && (
              <div className="rounded-2xl border border-amber-300 bg-amber-50/60 p-5" data-testid="mm-navratri-note">
                <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1.5">
                  <Info size={16} weight="duotone" /> A quick note on Navratri
                </div>
                <p className="text-sm text-[#3F2D1B] leading-relaxed">{result.navratri_note}</p>
              </div>
            )}

            {/* Funnel teaser — muhurat users → 20-Year Report */}
            <PremiumTeaser
              testid="mm-premium-teaser"
              topLine={<>
                A great date is a start.<br/>
                <span className="text-amber-300">A great decade is what you actually want.</span>
              </>}
              subLine={<>
                Your muhurat search revealed <b className="text-amber-100">{result.total_auspicious_dates} auspicious date{result.total_auspicious_dates === 1 ? "" : "s"}</b> in
                {" "}{result.year}. Now unlock a 55+ page 20-Year Report for the {result.boy?.name || "groom"} — with year-by-year
                career, marriage, wealth &amp; health scores, dasha-bhukti timeline, 6 doshas and personalised remedies.
              </>}
            />
          </section>
        )}

        {/* "Ask any date" — floating bottom-left panel, only after results */}
        {result && (
          <div className="fixed bottom-4 left-4 z-40" data-testid="mm-ask-any-date-widget">
            {!askOpen ? (
              <button
                onClick={() => setAskOpen(true)}
                data-testid="mm-ask-open"
                className="flex items-center gap-2 rounded-full bg-[#3F2D1B] text-white shadow-lg px-4 py-2.5 text-sm hover:bg-[#5A3F26] transition">
                <Question size={16} weight="duotone" />
                Ask any date
              </button>
            ) : (
              <div className="bg-white rounded-2xl shadow-2xl border border-soft w-[min(92vw,380px)] p-4"
                data-testid="mm-ask-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-serif-display text-lg text-[#3F2D1B] flex items-center gap-2">
                    <Question size={16} weight="duotone" className="text-gold" />
                    Ask any date
                  </div>
                  <button onClick={() => { setAskOpen(false); setAskResult(null); }}
                    data-testid="mm-ask-close" className="text-muted hover:text-[#3F2D1B]">
                    <X size={18} />
                  </button>
                </div>
                <p className="text-[11px] text-muted mb-3">
                  Pick any calendar date. I&apos;ll tell you exactly which classical rules pass or
                  fail for this couple on that day — and the final call.
                </p>
                <form onSubmit={checkDate} className="flex gap-2">
                  <input type="date" value={askDate} onChange={(e) => setAskDate(e.target.value)}
                    className="flex-1 px-2 py-2 rounded-md border border-soft bg-bg text-sm"
                    data-testid="mm-ask-date" />
                  <button type="submit" disabled={askBusy}
                    className="btn-primary rounded-md px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="mm-ask-submit">
                    <MagnifyingGlass size={14} weight="duotone" />
                    {askBusy ? "…" : "Check"}
                  </button>
                </form>

                {askResult && (
                  <div className="mt-4 space-y-3" data-testid="mm-ask-result">
                    <div className={`rounded-lg p-3 ${askResult.verdict === "good" ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                      <div className="flex items-center gap-2">
                        {askResult.verdict === "good"
                          ? <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                          : <XCircle size={20} weight="fill" className="text-rose-600" />}
                        <div className={`font-serif-display text-lg ${askResult.verdict === "good" ? "text-emerald-900" : "text-rose-900"}`}>
                          {askResult.verdict === "good" ? "Good" : "Bad"}
                          <span className="text-xs text-muted ml-2 align-middle">
                            {new Date(askResult.date + "T00:00:00").toLocaleDateString(undefined,
                              { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-[#3F2D1B] mt-1.5 leading-relaxed">{askResult.summary}</p>
                      {askResult.abuja && (
                        <div className="mt-2 text-[11px] font-semibold text-amber-800 flex items-center gap-1">
                          <Star size={12} weight="fill" className="text-amber-600" />
                          Abuja Muhurat · {askResult.abuja.name}
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-soft p-3">
                      <div className="text-[10px] label-eyebrow text-muted mb-1.5">Panchang</div>
                      <div className="text-xs text-[#3F2D1B] grid grid-cols-2 gap-x-3 gap-y-0.5">
                        <div><b>Weekday:</b> {askResult.weekday}</div>
                        <div><b>Tithi:</b> {askResult.tithi}</div>
                        <div><b>Nakshatra:</b> {askResult.nakshatra}</div>
                        <div><b>Yoga:</b> {askResult.yoga}</div>
                        <div><b>Karana:</b> {askResult.karana}</div>
                        <div><b>Sun:</b> {askResult.sun_sign}</div>
                        <div><b>Moon:</b> {askResult.moon_sign}</div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-soft p-3 max-h-56 overflow-y-auto">
                      <div className="text-[10px] label-eyebrow text-muted mb-1.5">Rule-by-rule</div>
                      <ul className="space-y-1.5">
                        {askResult.checks.map((c, i) => (
                          <li key={i} className="text-[11px] flex items-start gap-1.5">
                            {c.status === "pass"
                              ? <CheckCircle size={12} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                              : <XCircle size={12} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />}
                            <span>
                              <span className="font-semibold text-[#3F2D1B]">{c.rule}</span>
                              <span className="text-muted"> — {c.detail}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SumCard({ icon, label, value, note }) {
  return (
    <div className="rounded-lg border border-soft bg-white/60 p-3">
      <div className="text-[10px] label-eyebrow text-muted flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-serif-display text-2xl text-[#3F2D1B] mt-1">{value}</div>
      {note && <div className="text-[10px] text-muted mt-0.5">{note}</div>}
    </div>
  );
}
